import React, { useState } from 'react';
import { Home, Package, ClipboardList, Settings, Truck, CheckCircle } from 'lucide-react';
import { useCRM } from '../../utils/CRMContext';
import { EnhancedDeal, FrameType } from '../../types';
import './ConvertToProjectModal.css';

interface ConvertToProjectModalProps {
  deals: EnhancedDeal[];
  onClose: () => void;
  userId: string;
  preselectedDeal?: EnhancedDeal; // For auto-popup when marking deal as won
}

export const ConvertToProjectModal: React.FC<ConvertToProjectModalProps> = ({
  deals,
  onClose,
  userId,
  preselectedDeal,
}) => {
  const { convertDealToProject } = useCRM();
  const [selectedDealId, setSelectedDealId] = useState<string | null>(
    preselectedDeal?.id || (deals.length === 1 ? deals[0].id || null : null)
  );
  const [frameType, setFrameType] = useState<FrameType>('custom');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedDeal = preselectedDeal || deals.find(d => d.id === selectedDealId);

  const handleConvert = async () => {
    if (!selectedDeal) {
      setError('Please select a deal to convert');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const projectId = await convertDealToProject(selectedDeal, userId, frameType);
      console.log('Created project:', projectId);
      onClose();
    } catch (err) {
      console.error('Error converting deal to project:', err);
      setError('Failed to convert deal to project');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="convert-project-modal-overlay" onClick={onClose}>
      <div className="convert-project-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Convert Deal to Project</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="modal-content">
          <p className="modal-description">
            {preselectedDeal 
              ? 'This deal has been marked as WON! Create a project to track production.'
              : 'Select a won deal to convert into a timber frame project. Default milestones will be created automatically.'
            }
          </p>

          {/* Frame Type Selection */}
          <div className="frame-type-selector">
            <h4>Project Type</h4>
            <div className="frame-type-options">
              <label className={`frame-type-option ${frameType === 'custom' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="frameType"
                  value="custom"
                  checked={frameType === 'custom'}
                  onChange={() => setFrameType('custom')}
                />
                <div className="frame-type-content">
                  <span className="frame-type-title"><Home size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Custom Timber Frames</span>
                  <span className="frame-type-desc">Full production workflow: Design → Production → Installation</span>
                </div>
              </label>
              <label className={`frame-type-option ${frameType === 'kit' ? 'selected' : ''}`}>
                <input
                  type="radio"
                  name="frameType"
                  value="kit"
                  checked={frameType === 'kit'}
                  onChange={() => setFrameType('kit')}
                />
                <div className="frame-type-content">
                  <span className="frame-type-title"><Package size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Kit Frames</span>
                  <span className="frame-type-desc">Simplified workflow: Procurement → Production → Shipment → Delivery</span>
                </div>
              </label>
            </div>
          </div>

          {!preselectedDeal && deals.length === 0 ? (
            <div className="no-deals">
              <p>No won deals available to convert.</p>
              <p className="hint">Mark a deal as "Won" to convert it to a project.</p>
            </div>
          ) : !preselectedDeal && (
            <div className="deals-list">
              {deals.map(deal => (
                <div 
                  key={deal.id}
                  className={`deal-option ${selectedDealId === deal.id ? 'selected' : ''}`}
                  onClick={() => deal.id && setSelectedDealId(deal.id)}
                >
                  <div className="deal-radio">
                    <input
                      type="radio"
                      name="deal"
                      checked={selectedDealId === deal.id}
                      onChange={() => deal.id && setSelectedDealId(deal.id)}
                    />
                  </div>
                  <div className="deal-info">
                    <span className="deal-title">{deal.title}</span>
                    <span className="deal-contact">{deal.contactName}</span>
                    <div className="deal-meta">
                      <span className="deal-type">{deal.projectType}</span>
                      <span className="deal-location">{deal.location}</span>
                    </div>
                  </div>
                  <div className="deal-value">
                    {formatCurrency(deal.value)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {selectedDeal && (
            <div className="project-preview">
              <h3>Project Preview</h3>
              <div className="preview-grid">
                <div className="preview-item">
                  <span className="preview-label">Name</span>
                  <span className="preview-value">{selectedDeal.title}</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">Client</span>
                  <span className="preview-value">{selectedDeal.contactName}</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">Value</span>
                  <span className="preview-value">{formatCurrency(selectedDeal.value)}</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">Location</span>
                  <span className="preview-value">{selectedDeal.location}</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">Type</span>
                  <span className="preview-value">{selectedDeal.projectType}</span>
                </div>
                <div className="preview-item">
                  <span className="preview-label">Status</span>
                  <span className="preview-value">Planning (Initial)</span>
                </div>
              </div>

              <div className="milestones-preview">
                <h4>Default {frameType === 'kit' ? 'Stages' : 'Milestones'}</h4>
                {frameType === 'custom' ? (
                  <ul>
                    <li>Design Approval (30 days)</li>
                    <li>Production Start (60 days)</li>
                    <li>Production Complete (120 days)</li>
                    <li>Installation Complete (150 days)</li>
                  </ul>
                ) : (
                  <ul>
                    <li><ClipboardList size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Procurement Start</li>
                    <li><Settings size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Production Complete</li>
                    <li><Truck size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Shipment Date</li>
                    <li><CheckCircle size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Delivery Complete</li>
                  </ul>
                )}
              </div>
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
        </div>

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="convert-btn"
            onClick={handleConvert}
            disabled={loading || !selectedDealId}
          >
            {loading ? 'Creating Project...' : 'Create Project'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConvertToProjectModal;
